import React from "react";
import { toUrl } from "../../lib/config";
import "../../assets/styles/contact-strip.scss";

export type ContactItem = {
  /** Başlık (ör. ADRES / Telefon / E-Posta) */
  title: string;
  /** İkon: ReactNode (SVG / img) ya da public yol (string) */
  icon: React.ReactNode | string;
  /** İçerik satırları – link verilirse <a> olarak basılır */
  lines: Array<
    | string
    | {
      text: string;
      href?: string; // tel:, mailto:, harita, vb.
      target?: string;
      rel?: string;
    }
  >;
};

type ContactStripProps = {
  className?: string;
};

const isLinkObj = (
  v: ContactItem["lines"][number]
): v is { text: string; href?: string; target?: string; rel?: string } =>
  typeof v !== "string";

export const PinIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" />
  </svg>
);

export const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.04-.24 11.36 11.36 0 0 0 3.56.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 7a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.22.2 2.42.57 3.56a1 1 0 0 1-.24 1.04l-2.2 2.2z" />
  </svg>
);

export const MailIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2
       2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4-8 5L4 8V6l8 5 8-5v2z"/>
  </svg>
);

/** İletişim bilgileri – 3 kolonlu responsive şerit */
const ContactStrip: React.FC<ContactStripProps> = ({ className }) => {
  // Varsayılan içerik (endpoint başarısız olursa kullanılacak)
  const DEFAULT_ITEMS: ContactItem[] = [
    {
      title: "ADRES",
      icon: <PinIcon />, // veya "/icons/pin.svg"
      lines: [
        "Yavuz Selim, 73045. Sk. 32/A, 46080",
        "Dulkadiroğlu/Kahramanmaraş",
      ],
    },
    {
      title: "Telefon",
      icon: <PhoneIcon />,
      lines: [
        { text: "(0344) 231 62 62", href: "tel:+903442316262" },
        { text: "444 46 00", href: "tel:+904444600" },
      ],
    },
    {
      title: "E-Posta",
      icon: <MailIcon />,
      lines: [{ text: "info@markasihospital.com", href: "mailto:info@markasihospital.com" }],
    },
  ];
  const [contactItems, setContactItems] = React.useState<ContactItem[]>(DEFAULT_ITEMS);
  const fetchedRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    // if (fetchedRef.current) return; // StrictMode'da ikinci kez tetiklenmeyi engelle
    // fetchedRef.current = true;
    const run = async () => {
      try {
        const res = await fetch(toUrl('/api/contact'));
        if (!res.ok) throw new Error('fetch_failed');
        const data = await res.json();
        const list = Array.isArray(data) ? data.slice().sort((a:any,b:any)=> (a.orderIndex??0)-(b.orderIndex??0)) : [];
        const mapped: ContactItem[] = list.map((it:any) => {
          // title ve lines endpoint'ten, iconUrl'ü KULLANMA – başlığa göre ikon ata
          const title: string = it.title || '';
          const icon = /posta/i.test(title)
            ? <MailIcon />
            : (/telefon/i.test(title) ? <PhoneIcon /> : <PinIcon />);
          const linesArr = Array.isArray(it.lines) ? it.lines.slice().sort((a:any,b:any)=> (a.orderIndex??0)-(b.orderIndex??0)) : [];
          const lines: ContactItem['lines'] = linesArr.map((ln:any) => ({ text: ln.text || '', href: ln.href || undefined }));
          return { title: title || '', icon, lines };
        });
        if (!cancelled && mapped.length) setContactItems(mapped);
      } catch {
        // default bırak
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);
  return (
    <section className={`contact-strip ${className ?? ""}`}>
      <div className="cs-container">
        <div className="cs-grid">
          {contactItems.map((it, idx) => (
            <article className="cs-item" key={idx}>
              <div className="cs-icon">
                {/* icon string ise <img> olarak bas */}
                {typeof it.icon === "string" ? (
                  <img src={it.icon} alt="" aria-hidden="true" />
                ) : (
                  it.icon
                )}
              </div>

              <h3 className="cs-title">{it.title}</h3>

              <div className="cs-lines" style={{ display: 'grid', gap: 0 }}>
                {it.lines.map((ln, i) =>
                  isLinkObj(ln) ? (
                    <a
                      key={i}
                      href={ln.href}
                      target={ln.target}
                      rel={ln.rel}
                      className="cs-link"
                    >
                      {ln.text}
                    </a>
                  ) : (
                    <p key={i}>{ln}</p>
                  )
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContactStrip;

